import { ehSalaDeWhatsapp } from './janelaDoWhatsapp';
import { comoLista } from './selecaoMultipla';

/**
 * Os filtros do Painel de Atendimentos: quem está falando, e por onde.
 *
 * Pedido da Gisele (2º bloco): poder filtrar por **nome do cliente, telefone,
 * registro, técnico e instância**.
 *
 * O técnico já tinha caixa própria (ele recorta os CARDS de operador, que é
 * outro eixo) e o canal e o período também. Estes utilitários cobrem os quatro
 * que faltavam, e todos recortam a mesma coisa: as conversas em atendimento e
 * a fila.
 *
 * ## Uma caixa para três campos, e não três caixas
 *
 * Nome, telefone e registro respondem à MESMA pergunta -- "quem é essa
 * pessoa?" --, e quem procura normalmente tem só um dos três na mão. Três
 * caixas obrigariam a acertar em qual delas digitar, e a barra do painel já
 * tem técnico, canal e período. A instância é enumeração, como o canal, então
 * ela é um seletor.
 *
 * ## O telefone casa só por dígitos
 *
 * O operador digita "99765-4321" ou "(19) 99765" e o banco guarda
 * "5519997654321". Comparar texto cru não acharia nada, e o filtro pareceria
 * quebrado -- que é exatamente o tipo de defeito silencioso que esta tela já
 * teve. Os dois lados são reduzidos a dígitos antes de comparar.
 */

/** Só os dígitos, para o telefone casar com o que o operador digita. */
const soDigitos = (valor) => String(valor ?? '').replace(/\D/g, '');

const normalizar = (valor) => String(valor ?? '').trim().toLowerCase();

/*
 * ## As DUAS formas da mesma conversa, e o defeito que isso causou
 *
 * Os dois cartões recebem o cliente de formas diferentes, de endpoints
 * diferentes:
 *
 * - **em atendimento** (`/rooms/active-dashboard`) é um payload montado à mão
 *   na api: `client_name`, `client_code`, `marca`.
 * - **a fila** (`/rooms/waiting`) é o `Room` inteiro, serializado com as
 *   relações: `client.name`, `client.code`, `whatsapp_number.rotulo`.
 *
 * Estes filtros nasceram lendo só a primeira forma. O resultado, que foi para
 * produção em 14/10/2026 e eu só descobri escrevendo o teste do filtro por
 * cartão: **na fila, procurar por nome ou por registro não achava nada**, e o
 * seletor de instância não recortava a fila -- sem erro nenhum, só uma lista
 * que não reagia. O telefone funcionava nos dois por acidente: a coluna
 * `whatsapp_user_number` existe na sala.
 *
 * Os acessores abaixo existem para que a resposta a "onde está o nome do
 * cliente?" more em UM lugar. Ver [[duas-fontes-para-a-mesma-tela]].
 */

/** O nome do cliente, nas duas formas de payload. */
const nomeDoCliente = (conversa) => conversa?.client_name ?? conversa?.client?.name;

/** O registro (código) do cliente, nas duas formas. */
const registroDoCliente = (conversa) => conversa?.client_code ?? conversa?.client?.code;

/**
 * O rótulo da instância, nas duas formas.
 *
 * A mesma ordem de `utils/marcaDaSala`, e de propósito: são a mesma pergunta,
 * e divergir faria o chip da marca e o filtro por instância discordarem na
 * mesma linha.
 */
const marcaDaConversa = (conversa) => conversa?.whatsapp_number?.rotulo ?? conversa?.marca;

/**
 * A conversa casa o texto procurado?
 *
 * Procura em nome do cliente, registro e telefone. Busca vazia aceita tudo --
 * caixa em branco não é filtro.
 */
export const conversaCasaABusca = (conversa, busca) => {
    const procurado = normalizar(busca);

    if (procurado === '') return true;
    if (!conversa) return false;

    const nome = normalizar(nomeDoCliente(conversa));
    const registro = normalizar(registroDoCliente(conversa));

    if (nome.includes(procurado) || (registro !== '' && registro.includes(procurado))) {
        return true;
    }

    // O telefone por dígitos, e só quando o que foi digitado TEM dígito:
    // sem esta guarda, procurar por "ana" viraria uma busca por string vazia
    // dentro do telefone, que casa sempre.
    const digitados = soDigitos(busca);

    if (digitados === '') return false;

    return soDigitos(conversa.whatsapp_user_number).includes(digitados);
};

/** O valor do seletor que significa "não filtrar por origem". */
export const TODAS_AS_ORIGENS = '';

/**
 * O chat do site como ORIGEM, ao lado das instâncias de WhatsApp.
 *
 * Decisão do dono do produto em 14/10: o seletor separa a conversa pelas
 * instâncias que existirem **e também pelo chat do site**. Faz sentido porque
 * é a mesma pergunta -- "por onde esta pessoa chegou?" --, e um seletor que
 * lista MAX e SysFar mas não lista o site deixa a maior parte do tráfego sem
 * resposta.
 *
 * É uma string impossível como rótulo de número (o cadastro não aceita `__`),
 * para nunca colidir com uma marca de verdade.
 */
export const CHAT_DO_SITE = '__chat-do-site__';

/** O rótulo do chat do site no seletor. */
export const ROTULO_DO_CHAT_DO_SITE = 'Chat do site';

/**
 * Por onde a conversa chegou.
 *
 * - chat do site -> `CHAT_DO_SITE`
 * - WhatsApp com marca -> o rótulo da marca
 * - WhatsApp SEM marca -> `null`
 *
 * O terceiro caso não é teoria: são as salas históricas da camada antiga, e
 * qualquer conversa cujo número tenha perdido o rótulo. Elas não têm origem
 * selecionável, então só aparecem em "Todas" -- que é o certo. Dizer que são
 * do site seria mentir sobre o canal, e inventar uma marca seria pior.
 */
export const origemDaConversa = (conversa) => {
    if (!ehSalaDeWhatsapp(conversa)) return CHAT_DO_SITE;

    const marca = String(marcaDaConversa(conversa) ?? '').trim();

    return marca === '' ? null : marca;
};

/**
 * A conversa veio da origem escolhida?
 *
 * Origem é a instância de WhatsApp (SysFar, MAX) **ou o chat do site** -- a
 * mesma pergunta, "por onde esta pessoa chegou?".
 *
 * Comparação sem caixa nem acento para as marcas, porque o rótulo é digitado
 * pelo administrador no cadastro do número e ninguém garante a grafia entre
 * o cadastro e a opção escolhida. O `CHAT_DO_SITE` é constante nossa, então
 * compara exato.
 */
export const conversaCasaAOrigem = (conversa, origem) => {
    if (!origem || origem === TODAS_AS_ORIGENS) return true;

    const daConversa = origemDaConversa(conversa);

    if (origem === CHAT_DO_SITE) return daConversa === CHAT_DO_SITE;
    if (daConversa === CHAT_DO_SITE || daConversa === null) return false;

    return normalizar(daConversa) === normalizar(origem);
};

/**
 * As origens que aparecem no seletor, tiradas das conversas em tela.
 *
 * Montada do que existe AGORA, e não de uma consulta à tela de números: o
 * painel é de tempo real, e oferecer uma marca que não está em atendimento só
 * daria um filtro que devolve vazio.
 *
 * As marcas vêm em ordem alfabética e o **chat do site vai por último**, e não
 * misturado entre elas: ele não é uma marca, é o outro canal. No fim da lista
 * ele também fica sempre no mesmo lugar, quantas marcas existam -- quem usa o
 * painel todo dia acerta no músculo.
 *
 * @param {Array} conversas
 * @returns {string[]}
 */
export const origensDasConversas = (conversas) => {
    const marcas = new Set();
    let temSite = false;

    (Array.isArray(conversas) ? conversas : []).forEach((c) => {
        // Buraco na lista não é conversa, e muito menos chat do site: sem esta
        // guarda um `null` vindo de uma serialização torta acrescentaria a
        // opção "Chat do site" a um cartão que não tem nenhum.
        if (!c) return;

        const origem = origemDaConversa(c);

        if (origem === CHAT_DO_SITE) temSite = true;
        // `null` é WhatsApp sem marca: não vira opção, porque não há o que
        // escrever no seletor. A conversa continua aparecendo em "Todas".
        else if (origem !== null) marcas.add(origem);
    });

    const ordenadas = [...marcas].sort((a, b) => a.localeCompare(b, 'pt'));

    return temSite ? [...ordenadas, CHAT_DO_SITE] : ordenadas;
};

/**
 * Aplica os dois filtros de uma vez.
 *
 * @param {Array} conversas
 * @param {{busca?: string, origem?: string}} filtros
 */
export const filtrarConversasDoPainel = (conversas, { busca = '', origem = TODAS_AS_ORIGENS } = {}) =>
    (Array.isArray(conversas) ? conversas : []).filter(
        (c) => conversaCasaABusca(c, busca) && conversaCasaAOrigem(c, origem)
    );

/**
 * O TÉCNICO do grupo casa o texto procurado?
 *
 * Existe porque "Conversas em atendimento" é agrupado por técnico, e aí o nome
 * dele é uma das coisas que a pessoa digita naquela caixa -- esperar que ela
 * saiba que técnico se procura em outro lugar da tela é esperar demais.
 *
 * Busca vazia aceita tudo, como nas demais: caixa em branco não é filtro.
 */
export const tecnicoCasaABusca = (grupo, busca) => {
    const procurado = normalizar(busca);

    if (procurado === '') return true;

    return normalizar(grupo?.operator?.name).includes(procurado);
};

/**
 * Os grupos de "em atendimento" recortados por uma busca só.
 *
 * ## A regra, que não é obvia
 *
 * Se o TÉCNICO casa, o grupo vem INTEIRO -- procurar "Euller" tem de mostrar
 * tudo que o Euller está atendendo, e não só as conversas cujo cliente também
 * se chame Euller. Se o técnico não casa, sobram as conversas dele que casam
 * pelo cliente.
 *
 * Sem essa distinção, a busca por técnico devolveria o grupo com o cabeçalho
 * dele e zero conversas embaixo -- parecendo que ele não atende nada.
 *
 * `active_count` é refeito a partir do que sobrou: deixar o número do servidor
 * faria o cabeçalho do técnico dizer 3 com uma conversa embaixo. E grupo que
 * ficou sem conversa sai da lista -- cabeçalho com nada embaixo é ruído.
 *
 * @param {Array} grupos
 * @param {{busca?: string, origem?: string, passaNoCanal?: Function}} filtros
 */
export const filtrarAtendimentosDoPainel = (
    grupos,
    { busca = '', origem = TODAS_AS_ORIGENS, passaNoCanal = () => true } = {}
) => (Array.isArray(grupos) ? grupos : [])
    .map((grupo) => {
        const doCanal = (grupo.chats || []).filter((chat) => passaNoCanal(chat));
        const buscaDoCliente = tecnicoCasaABusca(grupo, busca) ? '' : busca;
        const chats = filtrarConversasDoPainel(doCanal, { busca: buscaDoCliente, origem });

        return { ...grupo, chats, active_count: chats.length };
    })
    .filter((grupo) => grupo.chats.length > 0);

/* ------------------------------------------------------------------ *
 * O cartão de AGENTES: busca por nome e filtro de presença.
 * ------------------------------------------------------------------ */

/**
 * O valor do seletor que significa "não filtrar por presença".
 */
export const TODA_PRESENCA = '';

/**
 * Online no SISTEMA é `users.status` 1 ou 2.
 *
 * Os dois valores significam sessão aberta (1 online, 2 ocupado/ausente), e é
 * assim que a bolinha da coluna "Sistema" já pintava antes deste filtro
 * existir. Repetir a regra aqui com outro corte faria a linha mostrar a
 * bolinha verde e o filtro "Online" esconder ela -- o pior resultado
 * possível, porque a tela se contradiria sem errar nada visível.
 */
export const statusEhOnline = (status) => status === 1 || status === 2;

export const agenteEstaOnline = (agente) => statusEhOnline(agente?.status);

/** Disponível no CHAT é `users.chat_status === 1`. */
export const agenteEstaDisponivelNoChat = (agente) => agente?.chat_status === 1;

/**
 * As opções do seletor de presença.
 *
 * São DOIS eixos diferentes, e não um: estar logado no sistema não é estar
 * aceitando conversa. A Gisele pediu "filtrar somente quem está online" (19/10)
 * olhando a tabela de técnicos -- e quem olha aquela tabela faz as duas
 * perguntas, porque as duas colunas estão lá, uma do lado da outra.
 */
export const PRESENCAS = [
    { value: TODA_PRESENCA, label: 'Todos' },
    { value: 'online', label: 'Online no sistema' },
    { value: 'disponivel', label: 'Disponível no chat' },
    { value: 'offline', label: 'Offline' },
];

/**
 * O agente casa a presença escolhida?
 *
 * Valor desconhecido aceita tudo, em vez de esvaziar a tabela: um seletor com
 * valor estranho (estado antigo no navegador, por exemplo) não deve apagar o
 * painel inteiro.
 */
export const agenteCasaAPresenca = (agente, presenca) => {
    if (presenca === 'online') return agenteEstaOnline(agente);
    if (presenca === 'offline') return !agenteEstaOnline(agente);
    if (presenca === 'disponivel') return agenteEstaDisponivelNoChat(agente);

    return true;
};

/** O agente casa o texto procurado? Busca vazia aceita tudo. */
export const agenteCasaABusca = (agente, busca) => {
    const procurado = normalizar(busca);

    if (procurado === '') return true;

    return normalizar(agente?.operator?.name).includes(procurado);
};

/**
 * Os agentes do painel recortados pelos dois filtros do cartão.
 *
 * @param {Array} agentes
 * @param {{busca?: string, presenca?: string}} filtros
 */
export const filtrarAgentesDoPainel = (
    agentes,
    { busca = '', presenca = TODA_PRESENCA, departamento = '' } = {}
) => (Array.isArray(agentes) ? agentes : []).filter(
    (a) => agenteCasaABusca(a, busca)
        && agenteCasaAPresenca(a, presenca)
        && agenteCasaAoDepartamento(a, departamento)
);

/**
 * O valor do seletor que significa "não filtrar por departamento".
 */
export const TODOS_OS_DEPARTAMENTOS = '';

/**
 * Os operadores cujo departamento a api não soube nomear.
 *
 * Não é teoria: `users.service_group_id` é NOT NULL, mas já apontou para grupo
 * de OUTRO tenant (defeito antigo), e a api devolve o nome nulo nesses casos
 * em vez de vazar o nome do grupo alheio. Sem esta opção, esses operadores só
 * apareceriam em "Todos" -- e quem está conferindo cadastro é exatamente quem
 * precisa achá-los.
 *
 * String impossível como nome de grupo, para nunca colidir com um de verdade.
 */
export const SEM_DEPARTAMENTO = '__sem-departamento__';

/** O rótulo do grupo sem nome no seletor. */
export const ROTULO_SEM_DEPARTAMENTO = 'Sem departamento';

/**
 * O departamento do agente, como valor de seletor.
 *
 * O **id** é a chave, e não o nome: dois grupos podem ter descrições
 * parecidas, e o id é o que a api já manda de graça.
 */
export const departamentoDoAgente = (agente) => {
    const id = agente?.service_group_id;
    const nome = String(agente?.service_group ?? '').trim();

    if (id === null || id === undefined || nome === '') return SEM_DEPARTAMENTO;

    return String(id);
};

/**
 * O agente é de ALGUM dos departamentos escolhidos?
 *
 * Aceita lista e valor único. Único é o estado antigo, de quando o seletor
 * era de escolha simples -- e ele volta de qualquer chamador que ainda não
 * tenha mudado. `comoLista` resolve os dois, e é o mesmo util que a Busca
 * Avançada usa: "o que é nada selecionado" não pode ter duas respostas no
 * mesmo sistema.
 *
 * Nada selecionado passa TUDO: é "filtro não preenchido", e não "filtro que
 * não aceita ninguém". Sem isso, abrir o painel esvaziaria a lista.
 *
 * `TODOS_OS_DEPARTAMENTOS` não precisa de tratamento próprio: ele é a string
 * vazia, e `comoLista` já descarta vazio. Filtrá-lo de novo aqui seria código
 * morto -- um mutante que o apagava sobreviveu a toda a suíte, o que é
 * exatamente como código morto se parece.
 */
export const agenteCasaAoDepartamento = (agente, departamento) => {
    const escolhidos = comoLista(departamento);

    if (escolhidos.length === 0) return true;

    return escolhidos.indexOf(departamentoDoAgente(agente)) > -1;
};

/**
 * Os departamentos que aparecem no seletor, tirados dos agentes em tela.
 *
 * Do que existe AGORA, como o seletor de instância do painel: oferecer um
 * departamento sem ninguém dentro só daria um filtro que devolve vazio.
 *
 * Em ordem alfabética, e "Sem departamento" por último -- ele não é um
 * departamento, é a ausência de um, e no fim fica sempre no mesmo lugar.
 *
 * @param {Array} agentes
 * @returns {Array<{value: string, label: string}>}
 */
export const departamentosDosAgentes = (agentes) => {
    const porValor = new Map();
    let temSemNome = false;

    (Array.isArray(agentes) ? agentes : []).forEach((a) => {
        if (!a) return;

        const valor = departamentoDoAgente(a);

        if (valor === SEM_DEPARTAMENTO) temSemNome = true;
        else if (!porValor.has(valor)) porValor.set(valor, String(a.service_group).trim());
    });

    const ordenados = [...porValor.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt'));

    return temSemNome
        ? [...ordenados, { value: SEM_DEPARTAMENTO, label: ROTULO_SEM_DEPARTAMENTO }]
        : ordenados;
};

/**
 * O texto da caixa fechada do seletor de departamentos.
 *
 * Com `multiple`, sem `renderValue` o MUI escreve os VALORES -- que aqui são
 * ids de grupo. "7, 9" não é um rótulo.
 *
 * Na mesma ordem em que as opções aparecem, e não na ordem em que foram
 * marcadas: marcar Suporte depois de Comercial não pode trocar o texto de
 * lugar.
 *
 * @param {Array<string>} marcados
 * @param {Array<{value: string, label: string}>} disponiveis
 * @returns {string}
 */
export const rotulosDosDepartamentosDoPainel = (marcados, disponiveis) => {
    const escolhidos = comoLista(marcados).map(String);
    const lista = Array.isArray(disponiveis) ? disponiveis : [];

    const rotulos = lista
        .filter((d) => escolhidos.indexOf(String(d.value)) > -1)
        .map((d) => d.label);

    /*
     * Um id marcado que sumiu da lista ainda precisa aparecer -- esconder o
     * que está filtrando deixaria a tabela curta sem explicação. Ele some
     * sozinho no efeito da tela, mas entre um render e outro é este texto que
     * a pessoa vê.
     */
    const orfaos = escolhidos.filter(
        (valor) => !lista.some((d) => String(d.value) === valor)
    );

    return rotulos.concat(orfaos).join(', ');
};

/* ------------------------------------------------------------------ *
 * "Conversas em atendimento" como LINHAS, e não agrupadas por técnico.
 * ------------------------------------------------------------------ */

/**
 * Achata os grupos de atendimento em uma linha por conversa.
 *
 * Pedido da Gisele (19/10/2026): "ao invés de agrupar por nome de operador,
 * cada conversa exatamente como temos hoje, mas o nome, em vez de ficar
 * acima, fica na linha do restante das informações".
 *
 * O agrupamento tinha um custo que só aparece num dia cheio: com oito
 * técnicos atendendo, metade do cartão era cabeçalho de técnico, e comparar
 * duas conversas quaisquer -- quem espera mais, quem tem mensagem não lida --
 * exigia pular de bloco em bloco. Em linhas, as conversas se comparam direto.
 *
 * O técnico vai JUNTO em cada linha (não some): é a mesma informação, só que
 * numa coluna em vez de num cabeçalho.
 *
 * A ordem dos grupos e das conversas dentro deles é preservada -- quem ordena
 * é a tela, pela coluna que o supervisor clicar.
 *
 * @param {Array} grupos como vêm de `/rooms/active-dashboard`
 * @returns {Array} uma linha por conversa, com `operator` dentro
 */
export const conversasEmLinha = (grupos) =>
    (Array.isArray(grupos) ? grupos : []).flatMap((grupo) => {
        if (!grupo) return [];

        return (Array.isArray(grupo.chats) ? grupo.chats : [])
            .filter(Boolean)
            .map((chat) => ({ ...chat, operator: grupo.operator || null }));
    });
